import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/date-input'
import { Modal } from '@/components/ui/modal'
import { useToast } from '@/hooks/useToast'
import { tasksApi } from '@/lib/api/tasks'
import { extractApiError } from '@/lib/api'
import { convertToApiFormat, formatDateForInput } from '@/lib/utils'
import type { TaskDateScope } from '@/types'

export interface AssignDatesTarget {
  scope: TaskDateScope
  num?: number
  productionDate?: string
  expiryDate?: string
}

interface AssignDatesModalProps {
  taskUuid: string
  target: AssignDatesTarget | null
  onClose: () => void
}

function scopeTitle(scope: TaskDateScope, num?: number): string {
  if (scope === 'task') return 'Даты задания'
  if (scope === 'box') return `Даты коробки №${num ?? ''}`
  return `Даты паллеты №${num ?? ''}`
}

export function AssignDatesModal({ taskUuid, target, onClose }: AssignDatesModalProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [productionDate, setProductionDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')

  useEffect(() => {
    if (!target) return
    setProductionDate(formatDateForInput(target.productionDate))
    setExpiryDate(formatDateForInput(target.expiryDate))
  }, [target])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error('Не выбрана область')
      const body: Parameters<typeof tasksApi.assignDates>[1] = {
        scope: target.scope,
      }
      const prod = convertToApiFormat(productionDate)
      const exp = convertToApiFormat(expiryDate)
      if (prod) body.production_date = prod
      if (exp) body.expiry_date = exp
      if (!body.production_date && !body.expiry_date) {
        throw new Error('Укажите дату производства и/или срок годности')
      }
      if (target.scope !== 'task' && target.num != null) {
        body.num = target.num
      }
      return (await tasksApi.assignDates(taskUuid, body)).data
    },
    onSuccess: (data) => {
      toast.success(`Обновлено кодов: ${data.updated_count}`)
      void queryClient.invalidateQueries({ queryKey: ['task', taskUuid] })
      void queryClient.invalidateQueries({ queryKey: ['task-km-codes', taskUuid] })
      void queryClient.invalidateQueries({ queryKey: ['task-boxes', taskUuid] })
      void queryClient.invalidateQueries({ queryKey: ['task-pallets', taskUuid] })
      onClose()
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  if (!target) return null

  return (
    <Modal isOpen onClose={onClose}>
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-semibold text-gray-900">{scopeTitle(target.scope, target.num)}</h3>
      </div>

      <Modal.Body>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Дата производства</label>
            <DateInput
              value={productionDate}
              onChange={setProductionDate}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Срок годности</label>
            <DateInput
              value={expiryDate}
              onChange={setExpiryDate}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose}>
          Отмена
        </Button>
        <Button type="button" isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
          Сохранить
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
